// The motion standard (docs/motion.md) for JS: durations (ms) and easings mirror the --dur-* / --ease-* tokens in
// styles/tokens.css. Short, one easing family, no overshoot; every JS animation is skipped under
// prefers-reduced-motion (lib/utils/motion).

export const DURATION = {
  /** press, hover, colour */
  fast: 120,
  /** menus, popovers, tab panels, toasts, crossfades */
  base: 180,
  /** drawers, dialogs, panels, the sliding tab indicator */
  slow: 220,
  /** a number rolling to its new value */
  roll: 320,
  /** a refused form's shake (functional) */
  emphasis: 320,
  /** a live row's tint fading out after it arrived */
  highlight: 1_200,
} as const;

/** Easings used from JS (= --ease-* in styles/tokens.css): one family, never an overshoot. */
export const EASE = {
  /** enter / settle (decelerate) */
  out: 'cubic-bezier(.2, .8, .2, 1)',
  /** leave (accelerate) */
  in: 'cubic-bezier(.4, 0, 1, 1)',
  /** move from one place to another */
  inOut: 'cubic-bezier(.4, 0, .2, 1)',
  /** kept for older callers: the same as `out` (no overshoot anywhere) */
  emphasis: 'cubic-bezier(.2, .8, .2, 1)',
  spring: 'cubic-bezier(.2, .8, .2, 1)',
  /** a form refusing a submit (= --ease-shake) */
  shake: 'cubic-bezier(.36, .07, .19, .97)',
} as const;

export const MOTION = {
  /** optimistic row removal fades out before it leaves the list (= the .row-out transition) */
  rowRemoveMs: DURATION.base,
  countUpMs: DURATION.roll,
  /** scan counters: faster, they change several times a second */
  countUpFastMs: DURATION.slow,
  /** pin / dot "bump" when a value changes */
  bumpMs: DURATION.slow,
  /** the bell swinging once when a critical alert comes in */
  wiggleMs: 480,
  /** a form card shaking when a submit is refused (= --dur-emphasis, the login's shake) */
  shakeMs: DURATION.emphasis,
  /** a side drawer / bottom sheet slides out this long before it unmounts (= .drawer.leaving) */
  drawerCloseMs: DURATION.base,
  /** the map's detail panel slides out this long before it unmounts (= .map-panel.leaving) */
  mapPanelCloseMs: DURATION.fast,
  /** account page: the "password diganti" check stays this long before the form is shown again */
  successHoldMs: 2_400,
  easeOut: EASE.out,
} as const;

/** Loading placeholders: shown only after a short wait (no flash on fast answers), then kept briefly. */
export const SKELETON = {
  delayMs: 150,
  minVisibleMs: 300,
} as const;

export const MAP_MOTION = {
  /** after a panel of the Ruang Kendali opened or folded (it slides for --dur-slow): then the map fits again */
  refitSettleMs: 320,
  truckGlideMs: 1_200,
  truckFadeMs: DURATION.base,
  /** arrival arc travelling from origin to destination */
  arrivalArcMs: 1_300,
  arrivalArcEase: 'cubic-bezier(.3,.6,.3,1)',
  /** the destination pin bumps when the arc reaches it */
  arrivalBumpDelayMs: 1_100,
  flyToSec: 0.6,
  flyToEaseLinearity: 0.3,
  fitSec: 0.5,
  /** "Ikuti kejadian": the camera pans to a location outside the visible area */
  followPanSec: 0.8,
  /** ring expanding at a location when a tag is read there (= the .read-ring animation) */
  readRingMs: 1_400,
  /** entrance: pins drop one after the other (= --stagger-pin), the warehouse last with a small bounce */
  pinDropMs: 450,
  pinStaggerMs: 40,
  /** entrance: the 7-day flow lines draw themselves once the pins are down */
  flowDrawMs: 900,
  /** a truck that just departed pops onto its route */
  truckAppearMs: 450,
  /** arrival: small burst at the destination when the arc reaches it (= the .arrive-burst animation) */
  burstMs: 700,
} as const;
